import {useState, useEffect} from 'react'
import {Outlet, useParams, useNavigate, useLocation} from 'react-router-dom'

// Peacock components
import {TabsNavigation, Tab, Button} from 'pepsico-ds'

// Custom Components
import {EntityViewHeader} from '@/components/common/EntityViewHeader/EntityViewHeader'

// Services
import {useCustomerData} from '@/services/Customer/customerService'

// Constants
import {ROUTES} from '@/utils/constants/routesConstants'

export const CustomerPage = () => {
  // Access the customerId parameter from the URL
  const {customerId} = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Fetch customer data using React Query hook
  const {data: customerData, isLoading, error} = useCustomerData(customerId)
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)

  // Effect to sync the selected tab with the current URL path
  useEffect(() => {
    const path = location.pathname
    // Check for the settings route first because it contains the profile route substring
    if (path.includes(ROUTES.CUSTOMER_VIEW_SETTINGS.replace(':customerId', customerId))) {
      setSelectedTabIndex(1) // Settings tab
    } else if (path.includes(ROUTES.CUSTOMER_VIEW.replace(':customerId', customerId))) {
      setSelectedTabIndex(0) // Profile tab
    }
  }, [location.pathname, customerId])

  // Handler for tab click events
  const handleTabClick = index => {
    // Update the selected tab state
    setSelectedTabIndex(index)
    // Navigate to the corresponding route based on tab index
    switch (index) {
      case 0:
        navigate(ROUTES.CUSTOMER_VIEW.replace(':customerId', customerId)) // Profile
        break
      case 1:
        navigate(ROUTES.CUSTOMER_VIEW_SETTINGS.replace(':customerId', customerId)) // Settings
        break
      default:
        break
    }
  }

  // Example breadcrumbs
  const breadcrumbItems = [
    {
      href: '/',
      label: 'Home',
    },
    {
      href: ROUTES.PROFILE_MANAGEMENT,
      label: 'Customers',
    },
    {
      href: ROUTES.CUSTOMER_VIEW.replace(':customerId', customerId),
      label: `${customerData?.customerInformation?.name || 'Customer ' + customerId}`,
    },
  ]

  const outletContext = {
    customerId,
    customerData,
    isLoading,
    error
  }

  const getTagColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "green";
      case "SUSPENDED":
        return "lightgray";
      case "INACTIVE":
        return "red";
      default:
        return "gray";
    }
  }

  // Show loading state
  if (isLoading) {
    return <div>Loading customer data...</div>
  }

  // Show error state
  if (error) {
    return <div>Error loading customer data: {error.message}</div>
  }

  // Check if customer data is null
  if (!customerData?.customerInformation?.customerId) {
    return (
      <div>
        <EntityViewHeader
          title=""
          breadcrumbs={breadcrumbItems}
          fields={[]}
        />
        <div className="flex flex-col items-center justify-center h-[500px]">
          <p className="text-2xl font-bold mb-4">Customer {customerId} does not exist!</p>
          <Button onClick={() => navigate(ROUTES.PROFILE_MANAGEMENT_CUSTOMERS)}>Go Back</Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <EntityViewHeader
        title={`${customerData?.customerInformation?.name}`}
        breadcrumbs={breadcrumbItems}
        tagText={customerData?.customerInformation?.customerStatus}
        tagColor={getTagColor(customerData?.customerInformation?.customerStatus)}
        fields={[
          {
            label: null, 
            value: customerData?.customerInformation?.customerUserId, 
          },
          {
            label: "Customer ID",
            value: customerData?.customerInformation?.customerId
          },
          {
            label: "User Type",
            value: customerData?.customerInformation?.userType
          },
        ]}
      />

      <div className="mb-6 mt-10">
        <TabsNavigation isDark={true} selectedIndex={selectedTabIndex}>
          <Tab className="bg-[#131313]" label="Profile" onClick={() => handleTabClick(0)} />
          <Tab className="bg-[#131313]" label="Settings" onClick={() => handleTabClick(1)} />
        </TabsNavigation>
      </div>

      <div className="">
        <Outlet context={outletContext} />
      </div>
    </div>
  )
}
